import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HolidayDto } from '../models/holiday.models';
import { NotesWithMentionsDto } from '../models/notes.models';
import {
  CreateTimeOffOfficePolicyRequest,
  CreateTimeOffRequestRequest,
  CreateTimeOffResourcePolicyRequest,
  SetCurrentYearAvailableRequest,
  TimeOffActionNotesDto,
  TimeOffBalanceDto,
  TimeOffHistoryDto,
  TimeOffOfficePolicyDto,
  TimeOffOverlapCheckResultDto,
  TimeOffRequestDto,
  TimeOffResourceOverrideDto,
  TimeOffResourcePolicyDto,
  UpdateTimeOffOfficePolicyRequest,
  UpdateTimeOffResourcePolicyRequest,
  VacationCarryOverDto,
  TimeOffRequestAdjustmentResultDto,
  UpdateTimeOffRequestRequest,
} from '../models/time-off.models';

@Injectable({
  providedIn: 'root',
})
export class TimeOffService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/timeoff`;

  // --- Admin endpoints (Resources page, requires CanSetResourceTimeOff) ---

  getByEmployeeId(employeeId: string): Observable<TimeOffRequestDto[]> {
    return this.http.get<TimeOffRequestDto[]>(
      `${this.apiUrl}/employee/${employeeId}`,
    );
  }

  getById(id: number): Observable<TimeOffRequestDto> {
    return this.http.get<TimeOffRequestDto>(`${this.apiUrl}/${id}`);
  }

  create(request: CreateTimeOffRequestRequest): Observable<TimeOffRequestDto> {
    return this.http.post<TimeOffRequestDto>(this.apiUrl, request);
  }

  /**
   * Asks whether time off for the employee over the inclusive range (yyyy-MM-dd) would overlap a
   * co-assigned resource's pending or approved time off (GET overlap-check). Approvers only: the
   * route needs Resources.TimeOff.Approval and answers 403 to anyone else, so callers gate on
   * the same permission before asking. The answer is advisory; nothing here blocks a save.
   */
  checkOverlap(
    employeeId: string,
    startDate: string,
    endDate: string,
  ): Observable<TimeOffOverlapCheckResultDto> {
    return this.http.get<TimeOffOverlapCheckResultDto>(
      `${this.apiUrl}/overlap-check`,
      { params: { employeeId, startDate, endDate } },
    );
  }

  // --- Self-service endpoints (User Profile page, any authenticated user) ---

  getMyTimeOff(): Observable<TimeOffRequestDto[]> {
    return this.http.get<TimeOffRequestDto[]>(`${this.apiUrl}/my`);
  }

  createMy(
    request: CreateTimeOffRequestRequest,
  ): Observable<TimeOffRequestDto> {
    return this.http.post<TimeOffRequestDto>(`${this.apiUrl}/my`, request);
  }

  // --- Holiday endpoints ---

  getHolidays(region?: string): Observable<HolidayDto[]> {
    return this.http.get<HolidayDto[]>(`${this.apiUrl}/holidays`, {
      params: region ? { region } : {},
    });
  }

  // --- Office policy endpoints (admin, requires Admin.TimeOffPolicy permissions) ---

  getAllOfficePolicies(): Observable<TimeOffOfficePolicyDto[]> {
    return this.http.get<TimeOffOfficePolicyDto[]>(
      `${this.apiUrl}/office-policies`,
    );
  }

  getOfficePolicyById(id: number): Observable<TimeOffOfficePolicyDto> {
    return this.http.get<TimeOffOfficePolicyDto>(
      `${this.apiUrl}/office-policies/${id}`,
    );
  }

  getOfficePoliciesByOffice(
    officeCode: string,
  ): Observable<TimeOffOfficePolicyDto[]> {
    return this.http.get<TimeOffOfficePolicyDto[]>(
      `${this.apiUrl}/office-policies/office/${officeCode}`,
    );
  }

  /**
   * The office's practice-area override rows only (practiceAreaCode set). The office defaults come
   * from getOfficePoliciesByOffice; an office with no overrides returns an empty list — there is no
   * per-practice-area backfill, because an absent row means "follows the office default".
   */
  getOfficePracticeAreaOverrides(
    officeCode: string,
  ): Observable<TimeOffOfficePolicyDto[]> {
    return this.http.get<TimeOffOfficePolicyDto[]>(
      `${this.apiUrl}/office-policies/office/${officeCode}/practice-area-overrides`,
    );
  }

  createOfficePolicy(
    request: CreateTimeOffOfficePolicyRequest,
  ): Observable<TimeOffOfficePolicyDto> {
    return this.http.post<TimeOffOfficePolicyDto>(
      `${this.apiUrl}/office-policies`,
      request,
    );
  }

  updateOfficePolicy(
    id: number,
    request: UpdateTimeOffOfficePolicyRequest,
  ): Observable<TimeOffOfficePolicyDto> {
    return this.http.put<TimeOffOfficePolicyDto>(
      `${this.apiUrl}/office-policies/${id}`,
      request,
    );
  }

  deleteOfficePolicy(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/office-policies/${id}`);
  }

  // --- Resource policy endpoints (requires Resources.TimeOffPolicy.Write) ---

  getAllResourcePolicies(): Observable<TimeOffResourcePolicyDto[]> {
    return this.http.get<TimeOffResourcePolicyDto[]>(
      `${this.apiUrl}/resource-policies`,
    );
  }

  getResourcePolicyById(id: number): Observable<TimeOffResourcePolicyDto> {
    return this.http.get<TimeOffResourcePolicyDto>(
      `${this.apiUrl}/resource-policies/${id}`,
    );
  }

  getResourcePoliciesByEmployee(
    employeeId: string,
  ): Observable<TimeOffResourcePolicyDto[]> {
    return this.http.get<TimeOffResourcePolicyDto[]>(
      `${this.apiUrl}/resource-policies/resource/${employeeId}`,
    );
  }

  getResourcePoliciesByOffice(
    officeCode: string,
  ): Observable<TimeOffResourceOverrideDto[]> {
    return this.http.get<TimeOffResourceOverrideDto[]>(
      `${this.apiUrl}/resource-policies/office/${officeCode}`,
    );
  }

  createResourcePolicy(
    request: CreateTimeOffResourcePolicyRequest,
  ): Observable<TimeOffResourcePolicyDto> {
    return this.http.post<TimeOffResourcePolicyDto>(
      `${this.apiUrl}/resource-policies`,
      request,
    );
  }

  updateResourcePolicy(
    id: number,
    request: UpdateTimeOffResourcePolicyRequest,
  ): Observable<TimeOffResourcePolicyDto> {
    return this.http.put<TimeOffResourcePolicyDto>(
      `${this.apiUrl}/resource-policies/${id}`,
      request,
    );
  }

  deleteResourcePolicy(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/resource-policies/${id}`);
  }

  // --- Balance endpoints ---

  /** Optional `year` query param, omitted when not provided (server defaults to current). */
  private yearParams(year?: number): { params: Record<string, string> } {
    return { params: year ? { year: year.toString() } : {} };
  }

  getBalance(employeeId: string, year?: number): Observable<TimeOffBalanceDto> {
    return this.http.get<TimeOffBalanceDto>(
      `${this.apiUrl}/balance/${employeeId}`,
      this.yearParams(year),
    );
  }

  getMyBalance(year?: number): Observable<TimeOffBalanceDto> {
    return this.http.get<TimeOffBalanceDto>(
      `${this.apiUrl}/my/balance`,
      this.yearParams(year),
    );
  }

  // --- History endpoints (finalized past-year snapshots; year defaults to current) ---

  getHistory(employeeId: string, year?: number): Observable<TimeOffHistoryDto> {
    return this.http.get<TimeOffHistoryDto>(
      `${this.apiUrl}/history/${employeeId}`,
      this.yearParams(year),
    );
  }

  getMyHistory(year?: number): Observable<TimeOffHistoryDto> {
    return this.http.get<TimeOffHistoryDto>(
      `${this.apiUrl}/my/history`,
      this.yearParams(year),
    );
  }

  // --- Carry-over endpoints (vacation carry-over + credit for a year) ---

  getCarryOver(
    employeeId: string,
    year?: number,
  ): Observable<VacationCarryOverDto> {
    return this.http.get<VacationCarryOverDto>(
      `${this.apiUrl}/carryover/${employeeId}`,
      this.yearParams(year),
    );
  }

  getMyCarryOver(year?: number): Observable<VacationCarryOverDto> {
    return this.http.get<VacationCarryOverDto>(
      `${this.apiUrl}/my/carryover`,
      this.yearParams(year),
    );
  }

  // --- Set-available endpoint (requires Resources.TimeOffPolicy.Write) ---

  /**
   * Sets the current-year vacation available balance to an absolute total (replaces, not adds);
   * returns the recomputed balance.
   */
  setCurrentYearAvailable(
    request: SetCurrentYearAvailableRequest,
  ): Observable<TimeOffBalanceDto> {
    return this.http.put<TimeOffBalanceDto>(
      `${this.apiUrl}/available`,
      request,
    );
  }

  // --- Status update endpoint (requires Resources.TimeOff.Approval) ---

  updateStatus(id: number, statusCode: string): Observable<TimeOffRequestDto> {
    return this.http.put<TimeOffRequestDto>(`${this.apiUrl}/${id}/status`, {
      statusCode,
    });
  }

  /**
   * Replaces the days a request covers (PUT {id}/days). Approvers' route: approved requests,
   * their own included, and their own pending one. `days` is the complete calendar-day set the
   * request should cover afterwards, holidays included; the server splits non-consecutive runs
   * into separate requests and reports them in the result.
   */
  updateRequestDays(
    id: number,
    days: string[],
  ): Observable<TimeOffRequestAdjustmentResultDto> {
    return this.http.put<TimeOffRequestAdjustmentResultDto>(
      `${this.apiUrl}/${id}/days`,
      { days } satisfies UpdateTimeOffRequestRequest,
    );
  }

  /** The self-service twin of updateRequestDays (PUT my/{id}/days): the caller's own request. */
  updateMyRequestDays(
    id: number,
    days: string[],
  ): Observable<TimeOffRequestAdjustmentResultDto> {
    return this.http.put<TimeOffRequestAdjustmentResultDto>(
      `${this.apiUrl}/my/${id}/days`,
      { days } satisfies UpdateTimeOffRequestRequest,
    );
  }

  /**
   * Replaces a request's days through the door the API expects. The routes apply the same
   * ownership and status rules once inside; they differ at the door, where the admin route
   * checks the Write permission before anything else and the self-service route checks
   * ownership. So an owner always goes self-service (their pending request needs no permission,
   * their approved one needs Write either way) and anyone else's request goes admin.
   */
  updateRequestDaysFor(
    id: number,
    days: string[],
    isOwn: boolean,
  ): Observable<TimeOffRequestAdjustmentResultDto> {
    return isOwn
      ? this.updateMyRequestDays(id, days)
      : this.updateRequestDays(id, days);
  }

  /**
   * Cancels a request through the door the API expects: an owner's pending request goes
   * self-service, which needs no permission and takes no note; anything else goes through the
   * admin status route, which needs the approval permission, followed by the manager note when
   * one was given. A failed note save does not undo or fail the cancel.
   */
  cancelRequestFor(
    id: number,
    request: { isOwn: boolean; statusCode?: string | null },
    note?: string,
  ): Observable<TimeOffRequestDto> {
    const pending = (request.statusCode || '').toUpperCase() === 'PENDING';
    if (request.isOwn && pending) {
      return this.cancelMyRequest(id);
    }
    return this.updateStatus(id, 'CANCELLED').pipe(
      switchMap((cancelled) =>
        note
          ? this.saveManagerNote(id, note, 'CANCELLED').pipe(
              map(() => cancelled),
              catchError(() => of(cancelled)),
            )
          : of(cancelled),
      ),
    );
  }

  /**
   * Cancels the signed-in user's own request (PUT my/{id}/status). The API accepts only
   * CANCELLED here, and only while the request is pending; an approved one is the approver's to
   * cancel through updateStatus.
   */
  cancelMyRequest(id: number): Observable<TimeOffRequestDto> {
    return this.http.put<TimeOffRequestDto>(`${this.apiUrl}/my/${id}/status`, {
      statusCode: 'CANCELLED',
    });
  }

  saveManagerNote(
    id: number,
    notes: string,
    statusCode?: string,
  ): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/${id}/manager-notes`, {
      notes,
      statusCode,
    });
  }

  getManagerNote(id: number): Observable<NotesWithMentionsDto> {
    return this.http.get<NotesWithMentionsDto>(
      `${this.apiUrl}/${id}/manager-notes`,
    );
  }

  getMyManagerNote(id: number): Observable<NotesWithMentionsDto> {
    return this.http.get<NotesWithMentionsDto>(
      `${this.apiUrl}/my/${id}/manager-notes`,
    );
  }

  getActionNotes(id: number): Observable<TimeOffActionNotesDto> {
    return this.http.get<TimeOffActionNotesDto>(
      `${this.apiUrl}/${id}/action-notes`,
    );
  }

  getMyActionNotes(id: number): Observable<TimeOffActionNotesDto> {
    return this.http.get<TimeOffActionNotesDto>(
      `${this.apiUrl}/my/${id}/action-notes`,
    );
  }
}
